'use client'

import Script from 'next/script'
import * as React from 'react'

import { useIsLoggedInQuery, useUserIdQuery } from 'auth/hooks'
import {
  initScript as googleSiteTagInitScript,
  scriptUrl as googleSiteTagScriptUrl,
  globalSiteInit,
} from '@repo/analytics/google-site-tag'
import { iubendaConsentEventName } from '@repo/analytics/iubenda'
import { useIubendaConfig } from './iubenda'
import type { IubendaConfig } from './iubenda'

interface GlobalSiteTagProps {
  disableAds?: boolean
  /** Skip consent gating on domains where Iubenda is not configured. */
  enableIubenda?: boolean
}

const iubendaDisabledConfig: IubendaConfig = { scriptProps: {}, variant: 'notApplicable' as const }

export const GlobalSiteTag = ({ disableAds = false, enableIubenda = true }: GlobalSiteTagProps) => {
  const resolvedIubendaConfig = useIubendaConfig()
  const { scriptProps, variant } = enableIubenda ? resolvedIubendaConfig : iubendaDisabledConfig
  const isIubendaManagedScript = scriptProps.type === 'text/plain'
  const deferredSourceProps = scriptProps['data-cf-defer']
    ? { 'data-cf-src': googleSiteTagScriptUrl }
    : {}

  const isLoggedInQuery = useIsLoggedInQuery()
  const userIdQuery = useUserIdQuery()
  const userId = isLoggedInQuery.data ? userIdQuery.data || null : null
  const initializedUserIdRef = React.useRef<string | null | undefined>(undefined)

  const initializeGlobalSiteTag = React.useCallback(() => {
    if (!isLoggedInQuery.isFetched || typeof window.gtag !== 'function') {
      return false
    }

    if (initializedUserIdRef.current !== userId) {
      globalSiteInit(userId)
      initializedUserIdRef.current = userId
    }

    return true
  }, [isLoggedInQuery.isFetched, userId])

  const handleGlobalSiteTagReady = React.useCallback(() => {
    initializeGlobalSiteTag()
  }, [initializeGlobalSiteTag])

  React.useEffect(() => {
    if (variant === 'locationNotSet' || initializeGlobalSiteTag()) {
      return
    }

    if (!isIubendaManagedScript) {
      return
    }

    const handleIubendaConsentEvent = () => {
      initializeGlobalSiteTag()
    }

    window.addEventListener(iubendaConsentEventName, handleIubendaConsentEvent)

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleIubendaConsentEvent)
    }
  }, [initializeGlobalSiteTag, isIubendaManagedScript, variant])

  if (variant === 'locationNotSet') {
    return null
  }

  return (
    <>
      {/* The stub runs early so gtag('event', ...) calls fired before the remote
          script loads queue in dataLayer instead of being dropped; the heavy
          gtag/js download itself stays deferred to lazyOnload. */}
      <Script
        id="global-site-tag-script-init"
        strategy="afterInteractive"
        onReady={isIubendaManagedScript ? undefined : handleGlobalSiteTagReady}
        {...scriptProps}
      >
        {googleSiteTagInitScript({ disableAds })}
      </Script>
      <Script
        id="global-site-tag-script"
        src={googleSiteTagScriptUrl}
        strategy="lazyOnload"
        {...scriptProps}
        {...deferredSourceProps}
      />
    </>
  )
}
